import React, { useState } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Paper,
  Typography,
  Avatar,
  Drawer,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Help as HelpIcon
} from '@mui/icons-material';

const SearchBar = ({
  placeholder = "Hinted search text",
  value = "",
  onChange = () => {},
  onSearch = () => {},
  onClear = () => {},
  variant = "default", // default, modal, withAvatar
  showMenu = true,
  showBack = false,
  showAvatar = false,
  avatarSrc = "",
  avatarLetter = "A",
  fullWidth = true,
  disabled = false,
  sx = {},
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);

  // Sync with external value prop changes
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    if (inputValue.trim()) {
      onSearch(inputValue.trim());
    }
  };

  const handleClear = () => {
    setInputValue("");
    onChange("");
    onClear();
  };

  const handleMenuClick = () => {
    setDrawerOpen(true);
  };

  const handleAvatarClick = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleBackClick = () => {
    // Handle back navigation - could emit event or callback
    console.log('Back navigation triggered');
  };

  // Base styles matching Figma design
  const getSearchBarStyles = () => {
    const isModal = variant === 'modal';
    
    return {
      '& .MuiOutlinedInput-root': {
        height: '56px',
        borderRadius: isModal ? '0px' : '28px',
        backgroundColor: isModal ? 'transparent' : '#F3EDF7',
        border: isModal ? 'none' : '1px solid #E7DFEC',
        '& fieldset': {
          border: 'none',
        },
        '&:hover fieldset': {
          border: 'none',
        },
        '&.Mui-focused fieldset': {
          border: 'none',
        },
        ...(isModal && {
          borderBottom: '1px solid #E7DFEC',
          paddingBottom: '8px'
        })
      },
      '& .MuiInputBase-input': {
        padding: '16px 8px',
        fontSize: '16px',
        fontWeight: 400,
        color: '#49454F',
        '&::placeholder': {
          color: '#79747E',
          opacity: 1,
        }
      }
    };
  };

  // Render start adornment based on variant
  const renderStartAdornment = () => {
    if (showBack && variant === 'modal') {
      return (
        <InputAdornment position="start">
          <IconButton
            onClick={handleBackClick}
            sx={{ color: '#49454F', ml: -1 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </InputAdornment>
      );
    }
    
    if (showMenu) {
      return (
        <InputAdornment position="start">
          <IconButton
            onClick={handleMenuClick}
            sx={{ color: '#49454F', ml: -1 }}
          >
            <MenuIcon />
          </IconButton>
        </InputAdornment>
      );
    }
    
    return null;
  };

  // Render end adornment based on variant
  const renderEndAdornment = () => {
    if (variant === 'modal' && inputValue) {
      return (
        <InputAdornment position="end">
          <IconButton
            onClick={handleClear}
            sx={{ color: '#49454F', mr: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </InputAdornment>
      );
    }

    if (showAvatar && variant === 'withAvatar') {
      return (
        <InputAdornment position="end">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={handleSearch}
              sx={{ color: '#49454F' }}
            >
              <SearchIcon />
            </IconButton>
                        <Avatar
              sx={{ 
                width: 32, 
                height: 32, 
                backgroundColor: '#6F2CC4',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
              src={avatarSrc}
              onClick={handleAvatarClick}
            >
              {!avatarSrc && avatarLetter}
            </Avatar>
          </Box>
        </InputAdornment>
      );
    }

    return (
      <InputAdornment position="end">
        <IconButton
          onClick={handleSearch}
          sx={{ color: '#49454F', mr: -1 }}
        >
          <SearchIcon />
        </IconButton>
      </InputAdornment>
    );
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto', ...sx }}>
      <TextField
        value={inputValue}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        variant="outlined"
        fullWidth={fullWidth}
        disabled={disabled}
        InputProps={{
          startAdornment: renderStartAdornment(),
          endAdornment: renderEndAdornment()
        }}
        sx={{
          ...getSearchBarStyles(),
          ...(disabled && {
            opacity: 0.6,
            pointerEvents: 'none'
          })
        }}
        {...props}
      />

      {/* Navigation Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Navigation Menu
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            <ListItem button onClick={() => setDrawerOpen(false)}>
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem button onClick={() => setDrawerOpen(false)}>
              <ListItemIcon>
                <AnalyticsIcon />
              </ListItemIcon>
              <ListItemText primary="Analytics" />
            </ListItem>
            <ListItem button onClick={() => setDrawerOpen(false)}>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Users" />
            </ListItem>
            <ListItem button onClick={() => setDrawerOpen(false)}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
            <Divider sx={{ my: 1 }} />
            <ListItem button onClick={() => setDrawerOpen(false)}>
              <ListItemIcon>
                <HelpIcon />
              </ListItemIcon>
              <ListItemText primary="Help & Support" />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={() => setProfileMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => setProfileMenuAnchor(null)}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setProfileMenuAnchor(null)}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Account Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => setProfileMenuAnchor(null)}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sign Out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SearchBar;